import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw, Home } from "lucide-react";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const isChunkError = (error: Error | null): boolean => {
  if (!error) return false;
  const msg = error.message || '';
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('Failed to fetch') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('ChunkLoadError')
  );
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // Auto-reload once for chunk loading failures
    if (isChunkError(error)) {
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return;
      }
    }
  }

  handleRetry = () => {
    // Clear the reload flag so future chunk errors can auto-reload
    sessionStorage.removeItem('chunk_reload');
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    sessionStorage.removeItem('chunk_reload');
    window.location.reload();
  };

  handleGoHome = () => {
    sessionStorage.removeItem('chunk_reload');
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const chunkError = isChunkError(this.state.error);

      return (
        <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden bg-background p-6 text-foreground">
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_45%_40%_at_50%_40%,hsl(var(--primary)/0.08),transparent_70%)]" />
          <div className="relative w-full max-w-[480px] rounded-[20px] border border-[hsl(var(--ec-line-strong))] bg-card p-7 shadow-[0_30px_80px_-24px_rgba(0,0,0,0.85)]">
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-mint))]">
              {chunkError ? "Update" : "Error"}
            </p>
            <h2 className="mt-3 text-[24px] font-semibold leading-tight tracking-[-0.025em]">
              {chunkError ? "A new version is ready" : "Something went wrong"}
            </h2>
            <p className="mt-2 text-[14.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">
              {chunkError
                ? "The page needs a refresh to load the latest version. Nothing you did is lost."
                : "An unexpected error stopped this page. Refresh to try again, or go back to the start."}
            </p>
            {!chunkError && this.state.error && (
              <pre className="mt-4 max-h-28 overflow-auto rounded-[12px] border border-border bg-background/60 p-3 text-left font-mono text-[11.5px] leading-relaxed text-[hsl(var(--ec-muted))]">
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
              <button type="button" onClick={this.handleReload} className="ec-cta inline-flex h-11 items-center justify-center gap-2 rounded-[10px] px-5 text-[14px] font-semibold">
                <RefreshCw className="h-4 w-4" />
                {chunkError ? "Refresh page" : "Refresh"}
              </button>
              {!chunkError && (
                <button type="button" onClick={this.handleGoHome} className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-[hsl(var(--ec-line-strong))] px-5 text-[14px] font-medium transition-colors hover:border-[hsl(var(--ec-mint)/0.5)]">
                  <Home className="h-4 w-4" />
                  Home
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
